import { useEffect, useId, useRef, useState } from 'react';
import { t } from 'i18next';

import { ToggleChip } from '@/components/atoms/ToggleChip';

import styles from './WeekChips.module.scss';

export type WeekNumberObject = {
    weekNumber: number;
    year: number;
    interimReport: boolean;
    key: string;
};

/** Beyond this many weeks the rest collapse behind a toggle. */
const DEFAULT_COLLAPSE_AFTER = 10;

export interface WeekChipsProps {
    /** Called with the selected weeks whenever they change, and once on mount. */
    onChange?: (selectedWeeks: WeekNumberObject[]) => void;
    /** The weeks on offer, newest first. */
    weeks?: WeekNumberObject[];
    /** Weeks past this many are hidden until the reader asks for them. */
    collapseAfter?: number;
    /** Shown while the report is still loading, so the row does not appear later. */
    disabled?: boolean;
    /** Selects this week until the reader picks one. Omit to start on the newest week. */
    defaultWeekKey?: string;
    /**
     * Who the chips belong to, such as `clientId-locationId`. A new value drops a manual
     * pick and follows `defaultWeekKey` again. The page stays mounted across sites, and a
     * week chosen on one site is still in the client-wide list for the next.
     */
    selectionScope?: string;
}

function getInitialSelectedWeeks(
    pillDataObjects: WeekNumberObject[],
    defaultWeekKey?: string
): WeekNumberObject[] {
    const preferred = defaultWeekKey
        ? pillDataObjects.find((week) => week.key === defaultWeekKey)
        : undefined;
    if (preferred) {
        return [preferred];
    }
    const firstPill = pillDataObjects[0];
    return firstPill ? [firstPill] : [];
}

/**
 * Drops selections the incoming list no longer offers, falling back to the newest week.
 *
 * Validated against the **whole** list, never against the collapsed view. Checking the visible
 * slice instead is what silently discarded a selected older week and snapped the report back to
 * the newest one.
 */
function syncSelectedWeeks(
    current: WeekNumberObject[],
    pillDataObjects: WeekNumberObject[]
): WeekNumberObject[] {
    if (pillDataObjects.length === 0) {
        return [];
    }

    const firstPill = pillDataObjects[0];
    if (!firstPill) {
        return [];
    }

    const validSelections = current.filter((week) =>
        pillDataObjects.some((pill) => pill.key === week.key)
    );

    if (validSelections.length > 0) {
        return validSelections;
    }

    return [firstPill];
}

function areSelectionsEqual(a: WeekNumberObject[], b: WeekNumberObject[]): boolean {
    return a.length === b.length && a.every((week, index) => week.key === b[index]?.key);
}

/**
 * The week filter chips: any number of weeks picked for the lice report, and how many of
 * them are on screen.
 *
 * Collapsing lives here rather than in the parent on purpose: this component owns the
 * selection, so the two cannot disagree about which weeks exist. Split across two components
 * the parent re-derived a truncated list on every render and this one read that as "those weeks
 * are gone", discarding any selection beyond the first ten.
 */
export default function WeekChips(props: WeekChipsProps) {
    const {
        weeks: pillDataObjects = [],
        onChange: onPillListChange = () => {},
        collapseAfter = DEFAULT_COLLAPSE_AFTER,
        disabled = false,
        defaultWeekKey,
        selectionScope,
    } = props;
    const [selectedWeeks, setSelectedWeeks] = useState<WeekNumberObject[]>(() =>
        getInitialSelectedWeeks(pillDataObjects, defaultWeekKey)
    );
    const [showingAll, setShowingAll] = useState(false);
    const rowId = useId();
    const selectedWeeksRef = useRef(selectedWeeks);
    selectedWeeksRef.current = selectedWeeks;
    const hasPublished = useRef(false);
    const userChanged = useRef(false);
    const selectionScopeRef = useRef(selectionScope);

    useEffect(() => {
        // A week chosen on the previous site is still in the client-wide list. Drop that
        // manual pick before reading it, or it is published again and treated as a new one.
        if (selectionScopeRef.current !== selectionScope) {
            selectionScopeRef.current = selectionScope;
            userChanged.current = false;
        }
        const current = selectedWeeksRef.current;
        const nextSelection = userChanged.current
            ? syncSelectedWeeks(current, pillDataObjects)
            : getInitialSelectedWeeks(pillDataObjects, defaultWeekKey);
        const unchanged = areSelectionsEqual(current, nextSelection);

        /*
         * The first run always publishes, even when the selection did not change.
         *
         * This component is unmounted whenever the report switches to daily mode, so coming
         * back it starts again at the newest week — while the parent still holds whatever was
         * selected before. Staying quiet because "nothing changed here" left the table showing
         * the old weeks under a selector that claimed the newest one.
         */
        if (unchanged && hasPublished.current) {
            return;
        }

        hasPublished.current = true;
        if (!unchanged) {
            setSelectedWeeks(nextSelection);
        }
        onPillListChange(nextSelection);
    }, [defaultWeekKey, pillDataObjects, onPillListChange, selectionScope]);

    if (pillDataObjects.length === 0) {
        if (!disabled) {
            return null;
        }
        return <div className={styles.weekChips} />;
    }

    const isSelected = (week: WeekNumberObject) =>
        selectedWeeks.some((selected) => selected.key === week.key);

    const canCollapse = pillDataObjects.length > collapseAfter;
    /**
     * A selected week stays on screen even when it sits past the cut-off, so the reader can see
     * what the report is showing and switch it off again without expanding the list first.
     */
    const visiblePills =
        showingAll || !canCollapse
            ? pillDataObjects
            : pillDataObjects.filter((pill, index) => index < collapseAfter || isSelected(pill));

    const listHandler = (value: string, isOn: boolean) => {
        if (isOn) {
            const weekAdded = pillDataObjects.find((week) => week.key === value);
            if (!weekAdded) {
                return;
            }
            userChanged.current = true;
            const weeks = [...selectedWeeks, weekAdded];
            setSelectedWeeks(weeks);
            onPillListChange(weeks);
            return;
        }

        userChanged.current = true;
        const weekRemoved = selectedWeeks.filter((week) => week.key !== value);
        setSelectedWeeks(weekRemoved);
        onPillListChange(weekRemoved);
    };

    const formatPillText = (pillDataObject: WeekNumberObject): string => {
        const pillText =
            new Date().getFullYear() !== pillDataObject.year
                ? `${pillDataObject.weekNumber}/${pillDataObject.year}`
                : `${pillDataObject.weekNumber}`;

        const weekText = pillDataObject.interimReport ? `${pillText} (I)` : pillText;
        return `${t('common.labels.week')} ${weekText}`;
    };

    /**
     * Exactly what the row is not showing, so the count cannot drift from the pills that a
     * selection beyond the cut-off keeps on screen.
     */
    const hiddenCount = pillDataObjects.length - visiblePills.length;

    return (
        <>
            <div id={rowId} className={styles.weekChips}>
                {visiblePills.map((pillDataObject) => {
                    const selected = isSelected(pillDataObject);
                    return (
                        <ToggleChip
                            key={pillDataObject.key}
                            pressed={selected}
                            disabled={disabled}
                            value={pillDataObject.key}
                            onClick={(event) => {
                                event.preventDefault();
                                listHandler(event.currentTarget.value, !selected);
                            }}>
                            {formatPillText(pillDataObject)}
                        </ToggleChip>
                    );
                })}
                {/*
                 * The overflow control is a pill at the end of the row rather than a text button
                 * below it: it costs no vertical space between the weeks and the table, it reads
                 * in the same visual language as what it reveals, and `+4` says how many weeks
                 * are hidden — which "Show more" never did.
                 */}
                {/*
                 * Hidden when there is nothing left to reveal: selecting every week past the
                 * cut-off keeps them all on screen, which would leave a `+0` that does nothing.
                 * Still shown while expanded, so the list can be collapsed again.
                 */}
                {canCollapse && (showingAll || hiddenCount > 0) && (
                    <ToggleChip
                        dashed
                        pressed={showingAll}
                        disabled={disabled}
                        name="show_more"
                        aria-expanded={showingAll}
                        aria-controls={rowId}
                        // The name carries the count the pill shows, so "+4" on screen is
                        // also what a screen reader or voice control hears.
                        aria-label={
                            showingAll
                                ? t('organisms.liceReportControls.showLess')
                                : t('organisms.liceReportControls.showMoreCount', {
                                      hidden: hiddenCount,
                                  })
                        }
                        onClick={() => setShowingAll((shown) => !shown)}>
                        {showingAll
                            ? t('organisms.liceReportControls.showLess')
                            : `+${hiddenCount}`}
                    </ToggleChip>
                )}
            </div>
        </>
    );
}
